import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'
import { CapaDoRelatorio } from './CapaDoRelatorio'
import { codigoDoDocumento } from './codigoDoDocumento'
import { IdentificacaoDaMudanca } from './IdentificacaoDaMudanca'
import { RessalvaDeHistorico } from './RessalvaDeHistorico'
import { RodapeDoRelatorio } from './RodapeDoRelatorio'
import { SecaoDeTexto } from './SecaoDeTexto'
import { TabelaDeRotulos } from './TabelaDeRotulos'

interface Props {
  gmud: DetalheGmud
  /** Instante da emissão, já formatado e fixado por quem monta a página. */
  emitidoEm: string
}

/**
 * O RDM em quatro folhas — capa, formulário, implementação e teste, rollback.
 *
 * A ordem das seções, os títulos e o conteúdo de cada tabela são os do documento que sai do
 * sistema anterior (`resources/views/admin/pdfs/gmud.blade.php`). É a única coisa da
 * interface que continua parecida com ele, a pedido de quem usa: este documento é impresso,
 * anexado ao chamado e entregue à fiscalização, e já existe uma pilha dele arquivada.
 *
 * As quebras de página são nossas (uma `<section className="rdm-folha">` por folha), e não
 * do motor de impressão. É o que permite o rodapé numerado sair em todas as folhas sem
 * depender de `position: fixed`, que o navegador não repete de forma confiável.
 *
 * @param props a solicitação impressa e o instante da emissão
 * @returns o documento inteiro
 */
export function DocumentoRdm({ gmud, emitidoEm }: Props) {
  const rodape = {
    chamado: gmud.chamadoNumero?.trim() || TEXTOS.naoInformado,
    codigo: codigoDoDocumento(gmud),
    emitidoEm,
  }
  const migrada = gmud.origem === 'MIGRADO_V1'

  return (
    <article className="rdm-documento">
      <section className="rdm-folha" aria-label="Capa">
        <CapaDoRelatorio gmud={gmud} />
        <RodapeDoRelatorio {...rodape} pagina={1} />
      </section>

      <section className="rdm-folha" aria-label="Formulário de Requisição de Mudança">
        <h2 className="rdm-secao-titulo">Formulário de Requisição de Mudança (RDM)</h2>
        {/* Repetido de propósito: as folhas do RDM circulam soltas no processo, e a que se
            fotocopia é esta, não a capa. */}
        {migrada && <RessalvaDeHistorico gmud={gmud} />}
        <IdentificacaoDaMudanca gmud={gmud} />
        <SecaoDeTexto titulo="Objetivo/Justificativa da Mudança" texto={gmud.plano.justificativa} espacado />
        <RodapeDoRelatorio {...rodape} pagina={2} />
      </section>

      <section className="rdm-folha" aria-label="Implementação e teste">
        <h2 className="rdm-secao-titulo">Implementação</h2>
        <SecaoDeTexto titulo="Pré Requisitos para Implementação" texto={gmud.plano.preRequisitos} />
        <SecaoDeTexto
          titulo="Descrição das atividades da mudança"
          texto={gmud.plano.procedimentosImplementacao}
          espacado
        />

        <h2 className="rdm-secao-titulo rdm-espaco">Teste</h2>
        <TabelaDeRotulos titulo="Data da Implementação" linhas={linhasDoTeste(gmud)} />
        <SecaoDeTexto
          titulo="Descrição das atividades da mudança"
          texto={gmud.plano.evidenciaTesteDescricao}
          espacado
        />
        <RodapeDoRelatorio {...rodape} pagina={3} />
      </section>

      <section className="rdm-folha" aria-label="Rollback">
        <h2 className="rdm-secao-titulo">Rollback</h2>
        <SecaoDeTexto titulo="Plano de Rollback" texto={gmud.plano.planoRollback} />
        <SecaoDeTexto titulo="Teste de Volta" texto={gmud.plano.testeVolta} espacado />
        <RodapeDoRelatorio {...rodape} pagina={4} />
      </section>
    </article>
  )
}

/**
 * As linhas do bloco de teste.
 *
 * A faixa desta tabela anuncia "Data da Implementação" desde o documento antigo, e lá
 * nenhuma célula trazia data nenhuma — só o aprovador. Como a V2 guarda as duas datas,
 * a linha existe e é preenchida: a executada quando já houve implantação, a prevista
 * enquanto não houve. É completar o que a faixa promete, não acrescentar campo.
 *
 * "Usuário Aprovador" fica vazio em registro do processo novo, e isso está certo: aqui quem
 * aprova é o comitê, e a decisão se lê na linha do tempo da solicitação, com data e nome de
 * quem votou. O campo só tem valor no histórico importado, onde era texto livre digitado no
 * sistema anterior.
 *
 * @param gmud a solicitação impressa
 * @returns as duas linhas da tabela de teste
 */
function linhasDoTeste(gmud: DetalheGmud) {
  const implantacao = gmud.dataImplementacaoExecutada ?? gmud.dataImplementacaoPrevista
  return [
    { rotulo: 'Data da Implementação', valor: dataHora(implantacao, TEXTOS.naoInformado) },
    { rotulo: 'Usuário Aprovador', valor: gmud.aprovador?.trim() || TEXTOS.naoInformado },
  ]
}
